import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Table, Input, notification, Skeleton, Tag, Space, Drawer } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { debounce } from 'lodash';
import {
    getItemBlockRecipes,
    getRecipeStoresForItemBlock,
    markRecipeItemBlock,
    unblockRecipeItem,
    getMenusByRecipeForItemBlock,
} from '../../services/itemblockservice';
import {
    ItemBlockRecipeItem,
    ItemBlockStoreStatus,
    ItemBlockMenuLinkedDto,
} from '../../types/itemblocktypes';
import { useGlobalLoading } from '../common/globalloadingcontext';
import FormButton from '../common/formbutton';
import BlockStoreSelectorDrawer from './storeselectorblockdrawer';

const { Search } = Input;

interface Props {
    userGuid: string;
    activeTab: string;
}

const ItemBlockRecipesTab: React.FC<Props> = ({ userGuid, activeTab }) => {
    const { setGlobalLoading } = useGlobalLoading();
    const [data, setData] = useState<ItemBlockRecipeItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Drawer state (block/unblock)
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState<ItemBlockRecipeItem | null>(null);
    const [storesData, setStoresData] = useState<ItemBlockStoreStatus[]>([]);
    const [currentAction, setCurrentAction] = useState<'block' | 'unblock' | null>(null);

    // Linked menus drawer
    const [linkedMenusDrawerVisible, setLinkedMenusDrawerVisible] = useState(false);
    const [linkedMenus, setLinkedMenus] = useState<ItemBlockMenuLinkedDto[]>([]);
    const [selectedRecipeForMenus, setSelectedRecipeForMenus] = useState<ItemBlockRecipeItem | null>(null);

    const fetchRecipes = async (pageNumber = 1, size = pageSize, term = search) => {
        setLoading(true);
        try {
            const res = await getItemBlockRecipes(pageNumber, size, term);
            setData(res.data.items);
            setTotal(res.data.totalCount);
            setCurrentPage(pageNumber);
        } catch {
            notification.error({ message: 'Error', description: 'Failed to load recipes.' });
        } finally {
            setLoading(false);
        }
    };

    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setSearch(value);
            fetchRecipes(1, pageSize, value);
        }, 400),
        [pageSize] // eslint-disable-line react-hooks/exhaustive-deps
    );

    useEffect(() => {
        if (activeTab === 'recipes') {
            fetchRecipes(currentPage, pageSize, search);
        }
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchRecipes(currentPage, pageSize, search);
    }, [pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleManageStores = async (recipe: ItemBlockRecipeItem, action: 'block' | 'unblock') => {
        setDrawerLoading(true);
        setDrawerVisible(true);
        setSelectedRecipe(recipe);
        setCurrentAction(action);
        try {
            const res = await getRecipeStoresForItemBlock(
                recipe.recipeGuid,
                undefined,
                action === 'block' ? 'Available' : 'Blocked'
            );
            setStoresData(res.data);
        } catch {
            notification.error({ message: 'Error', description: 'Failed to load stores.' });
        } finally {
            setDrawerLoading(false);
        }
    };

    const handleConfirmStores = async (selectedStoreGuids: string[], action: 'block' | 'unblock') => {
        if (!selectedRecipe) return;
        setGlobalLoading(true);
        try {
            const request = {
                recipeGuid: selectedRecipe.recipeGuid,
                storeGuids: selectedStoreGuids,
                userGuid,
                reason: action === 'block' ? 'Blocked via UI' : 'Unblocked via UI',
            };
            if (action === 'block') await markRecipeItemBlock(request);
            else await unblockRecipeItem(request);

            await fetchRecipes(currentPage, pageSize, search);
            notification.success({
                message: 'Success',
                description: `${selectedRecipe.name} updated for ${selectedStoreGuids.length} stores (action: ${action}).`,
            });
        } catch {
            notification.error({ message: 'Error', description: `Failed to update ${selectedRecipe.name}.` });
        } finally {
            setGlobalLoading(false);
            setDrawerVisible(false);
            setSelectedRecipe(null);
        }
    };

    const handleViewLinkedMenus = async (recipe: ItemBlockRecipeItem) => {
        setSelectedRecipeForMenus(recipe);
        setLinkedMenusDrawerVisible(true);
        try {
            const res = await getMenusByRecipeForItemBlock(recipe.recipeGuid);
            setLinkedMenus(res.data);
        } catch {
            notification.error({ message: 'Error', description: 'Failed to load linked menus.' });
        }
    };

    const columns: ColumnsType<ItemBlockRecipeItem> = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        {
            title: 'Stores',
            key: 'stores',
            render: (_: unknown, record: ItemBlockRecipeItem) => (
                <Space style={{ display: 'flex', width: '100%' }}>
                    <div style={{ flex: 1, minWidth: 120 }}>Total Stores: {record.totalStores}</div>
                    <div style={{ flex: 1, minWidth: 160 }}>
                        <Tag color="red">Out of stock in {record.blockedCount} stores</Tag>
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                        <Tag color="green">In stock in {record.availableCount} stores</Tag>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'manage',
            render: (_: unknown, record: ItemBlockRecipeItem) => (
                <Space>
                    <FormButton title="Block" onClick={() => handleManageStores(record, 'block')} />
                    <FormButton title="Unblock" onClick={() => handleManageStores(record, 'unblock')} />
                    <FormButton title="View Linked Menus" onClick={() => handleViewLinkedMenus(record)} />
                </Space>
            ),
        },
    ];

    return (
        <>
            <Row gutter={16} style={{ marginBottom: '16px' }}>
                <Col>
                    <Search
                        placeholder="Search recipes"
                        onChange={e => debouncedSearch(e.target.value)}
                        style={{ width: 350 }}
                        allowClear
                    />
                </Col>
            </Row>

            {loading && data.length === 0 ? (
                <Skeleton active paragraph={{ rows: 5 }} />
            ) : (
                <Table<ItemBlockRecipeItem>
                    dataSource={data}
                    columns={columns}
                    loading={loading && data.length > 0}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        showTotal: (t, range) => `Showing ${range[0]}-${range[1]} of ${t} items`,
                        onChange: (page, size) => {
                            setPageSize(size!);
                            fetchRecipes(page, size!, search);
                        },
                    }}
                    rowKey="recipeGuid"
                />
            )}

            {selectedRecipe && currentAction && (
                <BlockStoreSelectorDrawer
                    visible={drawerVisible}
                    onClose={() => setDrawerVisible(false)}
                    onOk={handleConfirmStores}
                    itemName={selectedRecipe.name}
                    stores={storesData}
                    loading={drawerLoading}
                    action={currentAction}
                />
            )}

            <Drawer
                title={`Linked Menus for ${selectedRecipeForMenus?.name}`}
                width={650}
                onClose={() => setLinkedMenusDrawerVisible(false)}
                open={linkedMenusDrawerVisible}
                styles={{
                    header: {
                        color: 'white',
                        backgroundColor: 'var(--color-primary)',
                        fontWeight: 350,
                        fontSize: '14px',
                        border: 0,
                        minHeight: '40px',
                    },
                }}
            >
                <Table<ItemBlockMenuLinkedDto>
                    dataSource={linkedMenus}
                    columns={[
                        { title: 'Menu Name', dataIndex: 'name', key: 'name' },
                        { title: 'Menu Guid', dataIndex: 'menuGuid', key: 'menuGuid' },
                    ]}
                    rowKey="menuGuid"
                    pagination={{ pageSize: 10 }}
                />
            </Drawer>
        </>
    );
};

export default ItemBlockRecipesTab;